import {
  TabRouter,
  unstable_createStandardRouterNavigator,
  type NavigatorContentProps,
} from "@-/com.npmjs.expo-router";
import { ScreenContainer, Screen } from "@-/com.npmjs.react-native-screens";
import { StyleSheet, View } from "@-/com.npmjs.react-native";
import { useRef } from "react";

/**
 * Content for the keep-alive Levels navigator: renders each completion sub-route mounted inside a
 * `ScreenContainer`, marking only the active one active (`activityState: 2`) and the rest inactive
 * (`activityState: 0`). Once a route has been visited it stays mounted-but-hidden (and frozen when
 * screen freezing is on), so re-selecting a completion filter shows the already-rendered list
 * instantly — no remount, no blank flash.
 *
 * Sub-routes are mounted LAZILY: a route's content renders only once it has been the active route at
 * least once. Eagerly rendering all three completion sub-routes on the first Browse open mounted
 * three full `LevelsList`s at once — tripling the level-surface mount + LegendList layout work on the
 * one paint the user is waiting for, which was a large part of why Browse took seconds to show its
 * graphemes. Only the initially-active route ("All") pays that cost up front; "Incomplete"/"Complete"
 * mount the first time their pill is tapped, then stay alive.
 */
function LevelsKeepAliveContent({ descriptors, state }: NavigatorContentProps<object>) {
  // Route keys that have been active at least once — the set of sub-routes whose content is mounted.
  const activatedKeysRef = useRef<Set<string>>(new Set());

  return (
    <ScreenContainer enabled style={styles.container}>
      {state.routes.map((route, index) => {
        const isActive = index === state.index;
        if (isActive) {
          activatedKeysRef.current.add(route.key);
        }
        const hasBeenActivated = activatedKeysRef.current.has(route.key);
        return (
          <Screen
            activityState={isActive ? 2 : 0}
            enabled
            key={route.key}
            style={StyleSheet.absoluteFill}
          >
            <View collapsable={false} style={styles.scene}>
              {hasBeenActivated ? descriptors[route.key]?.render() : null}
            </View>
          </Screen>
        );
      })}
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  scene: { flex: 1 },
});

// `<Navigator>` (with a `.Screen` API) built from `LevelsKeepAliveContent` + `TabRouter`. A TabRouter
// keeps all declared screens permanently in `state.routes` (unlike a Stack/`Slot`, which unmounts the
// outgoing route on `router.replace`), which is what lets the content keep every route mounted.
const LevelsKeepAliveNavigator = unstable_createStandardRouterNavigator(
  LevelsKeepAliveContent,
  TabRouter,
);

/**
 * Keep-alive replacement for `<Slot />` in the Levels layout.
 *
 * The three completion sub-routes (`index` = All / `incomplete` / `complete`) each render their own
 * `LevelsListView`. A plain `<Slot />` renders only the active route and unmounts the previous one on
 * every `router.replace`, so switching completion pills re-mounted the outgoing route's `LegendList`
 * from empty — a blank flash. (We can't use a nested `MaterialTopTabs` here: a `MaterialTopTabs`
 * nested inside the parent browse pager fails to lay out its scene — the documented reason the layout
 * moved to `<Slot />` in the first place. This custom navigator has no pager UI, so it sidesteps that
 * bug while getting the same keep-mounted behaviour the parent browse tabs get from `MaterialTopTabs`
 * and the bottom tabs get from `freezeOnBlur`.)
 */
export function BrowseLevelsKeepAliveSlot() {
  return (
    <LevelsKeepAliveNavigator>
      <LevelsKeepAliveNavigator.Screen name="index" />
      <LevelsKeepAliveNavigator.Screen name="incomplete" />
      <LevelsKeepAliveNavigator.Screen name="complete" />
    </LevelsKeepAliveNavigator>
  );
}
